import { formatDisplayDateTime } from '../../common/dateTime/formatDisplayDateTime';
import { PolicySummary } from './historicalDataApi';

export interface HistoricalTableRow extends Record<string, unknown> {
  uniqueId: string;
  employerGroupName: string;
  policyNumber: string;
  surveyYear: string;
  fundingType: string;
  userName: string;
  surveyStartDate: string;
  surveyCompletedDate: string;
  surveyStatus: string;
  download: string;
  rawCustomerNumber: string;
  rawSurveyId: string;
  rawSurveyYear: string;
  rawEmployerGroupEIN: string | null;
}

function buildGroupKey(item: PolicySummary) {
  return `${item.customerNumber ?? ''}||${item.fundingType ?? ''}||${item.surveyYear ?? ''}`;
}

function normalizePolicyValue(item: PolicySummary) {
  const medId = (item.medIdCardNum ?? '').trim();
  return medId.length > 0 ? medId : null;
}

export function getPolicyTableRows(
  results: PolicySummary[],
  fallbackValue: string,
  defaultStatus: string,
): HistoricalTableRow[] {
  const grouped = new Map<string, { representative: PolicySummary; policies: Set<string> }>();

  results.forEach((item) => {
    const key = buildGroupKey(item);
    const existing = grouped.get(key);
    const normalizedPolicy = normalizePolicyValue(item);

    if (!existing) {
      const policies = new Set<string>();
      if (normalizedPolicy) {
        policies.add(normalizedPolicy);
      }
      grouped.set(key, { representative: item, policies });
      return;
    }

    if (normalizedPolicy) {
      existing.policies.add(normalizedPolicy);
    }
  });

  return Array.from(grouped.entries()).map(([key, value], index) => {return {
    uniqueId: `${key}-${index}`,
    employerGroupName: value.representative.employerGroupName ?? fallbackValue,
    policyNumber: value.policies.size > 0 ? Array.from(value.policies).join(', ') : fallbackValue,
    surveyYear: value.representative.surveyYear ?? fallbackValue,
    fundingType: value.representative.fundingType ?? fallbackValue,
    userName: value.representative.userName ?? '',
    surveyStartDate: formatDisplayDateTime(value.representative.surveyStartDate, ''),
    surveyCompletedDate: formatDisplayDateTime(value.representative.surveyCompletedDate, ''),
    surveyStatus: value.representative.surveyStatus ?? defaultStatus,
    download: value.representative.surveyStatus === 'Complete' ? 'Download' : '',
    rawCustomerNumber: value.representative.customerNumber ?? '',
    rawSurveyId: value.representative.surveyId != null ? String(value.representative.surveyId) : '',
    rawSurveyYear: value.representative.surveyYear ?? '',
    rawEmployerGroupEIN: value.representative.employerGroupEIN ?? null,
  }});
}

export function splitPolicyList(policyList: string, fallbackValue: string): string[] {
  if (!policyList || policyList === fallbackValue) {
    return [];
  }

  return policyList
    .split(/[,;]/)
    .map((value) => {return value.trim()})
    .filter((value) => {return value.length > 0});
}

export function filterTableRows(rows: HistoricalTableRow[], filter: string, minApplyLength = 3): HistoricalTableRow[] {
  const normalized = filter.trim().toLowerCase();

  if (normalized.length < minApplyLength) {
    return rows;
  }

  return rows.filter(
    (row) =>
      {return row.employerGroupName.toLowerCase().includes(normalized)
      || row.policyNumber.toLowerCase().includes(normalized)
      || row.fundingType.toLowerCase().includes(normalized)},
  );
}
